'use client'

import { liteClient as algoliasearch } from 'algoliasearch/lite'
import { InstantSearchNext } from 'react-instantsearch-nextjs'

import { env } from '@/env'
import { FACETS } from '@/views/Collection/CollectionFiltersDrawer'

import type { UiState } from 'instantsearch.js'
type FacetAttribute = (typeof FACETS)[number]

const facetToParamKey = (facet: FacetAttribute) => {
  return facet
    .replace(/^options\./, '')
    .replace(/^named_tags\./, '')
    .replace(/\./g, '_')
    .replace(/\(|\)/g, '')
    .toLowerCase() as FacetAttribute
}

type RouteParamKey = ReturnType<typeof facetToParamKey>
type RouteState = Record<RouteParamKey, string | undefined>

export const searchClient = algoliasearch(env.NEXT_PUBLIC_ALGOLIA_APP_ID, env.NEXT_PUBLIC_ALGOLIA_SEARCH_API_KEY)

export const indexName = 'shopify_products'

const stateToRouteFacets = (uiState: UiState) => {
  const indexState = uiState.shopify_products || {}

  return FACETS.reduce(
    (acc, facet) => {
      const key = facetToParamKey(facet)

      const refinements = indexState.refinementList?.[facet]

      acc[key] = refinements?.length ? refinements.join(',') : undefined

      return acc
    },
    {} as Record<FacetAttribute, string | undefined>,
  )
}

const routeToStateFacets = (route: RouteState) => {
  return FACETS.reduce(
    (acc, facet) => {
      const key = facetToParamKey(facet)

      acc[facet] = route[key]?.length ? route[key].split(',') : []

      return acc
    },
    {} as Record<string, string[]>,
  )
}

export function AlgoliaProvider({ children }: { children: React.ReactNode }) {
  return (
    <InstantSearchNext
      ignoreMultipleHooksWarning
      searchClient={searchClient}
      indexName={indexName}
      future={{ preserveSharedStateOnUnmount: true }}
      routing={{
        stateMapping: {
          stateToRoute(uiState) {
            return {
              page: uiState.shopify_products?.page,
              sortBy: uiState.shopify_products?.sortBy,
              ...stateToRouteFacets(uiState),
            }
          },
          routeToState(routeState) {
            return {
              shopify_products: {
                page: routeState.page,
                sortBy: routeState.sortBy,
                refinementList: {
                  ...routeToStateFacets(routeState),
                },
              },
            }
          },
        },
      }}
    >
      {children}
    </InstantSearchNext>
  )
}
